import React, { useMemo, useState } from "react";

import { Box, Textarea } from "@chakra-ui/react";
import { freezeAccountTotally } from "api/individual-user-management";
import { IFreezeAccountTotallyPayload } from "api/individual-user-management/types";
import { ReactComponent as InfoOutlineIcon } from "assets/icons/info-outline.svg";
import { ReactComponent as LinkIcon } from "assets/icons/link-rounded-red.svg";
import { useIndividuals } from "contexts/individuals-context";
import { customStyledSelectStyles } from "features/businesses/add-new-business/styles";
import {
  DEFAULT_FREEZE_UNFREEZE_FORM_DATA,
  OPTIONS_FOR_REASON_TO_DEACTIVATE_FREEZE_OR_CLOSE,
} from "features/businesses/business-details/consts";
import { IFreezeUnfreezeBusinessAccountFormData } from "features/businesses/business-details/content/accounts/types";
import { MIN_REASON_LENGTH } from "features/individuals/individual-details/consts";
import { transactionLimitModalHeaderCss } from "features/individuals/individual-details/styles";
import { useUploadDocument } from "hooks/useUploadDocument";
import { useParams } from "react-router-dom";
import { REGEX_VALUES, getUniqueFileName } from "utils";
import { Maybe } from "utils/general/maybe";

import { Button } from "ui/button";
import { customErrorToast, customSuccessToast } from "ui/custom-toast";
import { FileUpload } from "ui/file-upload";
import { Modal } from "ui/modal";
import { ModalHeader } from "ui/modal/modal-header";
import { defaultModalCss } from "ui/modal/styles";
import { StyledSelect } from "ui/styled-select";
import { IStyledSelectOption } from "ui/styled-select/types";
import { Heading3, Paragraph3 } from "ui/typography";

import { IApplyPostNoDebitModalProps } from "./types";

export const ApplyPostNoDebitModal = (props: IApplyPostNoDebitModalProps) => {
  const { isOpen, setIsOpen, handleToggleApplyPostNoDebitModal, walletId } =
    props;
  const [isApplying, setIsApplying] = useState(false);
  const { individualId } = useParams();
  const userId = individualId;
  const { handleGetIndividualDetails, handleGetIndividualAccounts } =
    useIndividuals();
  const { isUploading, uploadFile } = useUploadDocument();
  const [formData, setFormData] =
    useState<IFreezeUnfreezeBusinessAccountFormData>(
      DEFAULT_FREEZE_UNFREEZE_FORM_DATA
    );
  const [othersReason, setOthersReason] = useState("");
  const [isSelectMenuOpen, setIsSelectMenuOpen] = useState(false);

  const isFormDisabled = useMemo(() => {
    let isReasonDisabled = false;
    if (
      (formData.reason === "Others" &&
        othersReason.length < MIN_REASON_LENGTH) ||
      formData.reason.length < MIN_REASON_LENGTH
    ) {
      isReasonDisabled = true;
    }

    return isApplying || isUploading || isReasonDisabled;
  }, [isApplying, isUploading, formData, othersReason]);

  const handleCloseModal = () => {
    setFormData(DEFAULT_FREEZE_UNFREEZE_FORM_DATA);
    setOthersReason("");
    handleToggleApplyPostNoDebitModal();
  };

  const handleReasonChange = (evt: React.ChangeEvent<HTMLTextAreaElement>) => {
    const { value } = evt.target;
    if (!REGEX_VALUES.LETTERS_WITH_NUMBERS_COMMA_AND_HYPHEN_ONLY.test(value)) {
      return;
    }
    setOthersReason(value);
  };

  const handleSelectChange = (value: unknown, name?: string) => {
    const selectedValue = value as IStyledSelectOption;
    if (!name) {
      console.error("Error: Select input needs a name attribute!");
      return;
    }
    setFormData({
      ...formData,
      [name]: selectedValue.value,
    });
  };

  const handleFileChange = (name: string, file: File | null) => {
    setFormData({
      ...formData,
      [name]: file,
    });
  };

  const handleApplyPostNoDebit = async () => {
    if (!walletId) return;
    const { attachment } = formData;
    const reason =
      formData.reason === "Others" ? othersReason : formData.reason;
    setIsApplying(true);
    const payload: IFreezeAccountTotallyPayload = {
      walletId,
      freeze: true,
      reason,
    };
    try {
      if (attachment) {
        const {
          status,
          url,
          message: singleFileUploadMsg,
        } = await uploadFile({
          file: attachment as File,
          documentName: "others",
          companyName: getUniqueFileName(
            `${walletId}-freeze-individual-account-attachment`
          ),
        });
        if (status === "failed") {
          customErrorToast({
            message: singleFileUploadMsg,
          });
          setIsApplying(false);
          return;
        }
        payload["attachment"] = url;
      }
      const { error, message } = await freezeAccountTotally(payload);
      if (error) {
        customErrorToast({
          message,
        });
      } else {
        if (userId) {
          await handleGetIndividualDetails(userId as string);
          await handleGetIndividualAccounts({
            userId: userId as string,
          });
          customSuccessToast({
            message: "Account Frozen Successfully",
          });
        }
        handleCloseModal();
      }
    } catch (error) {
      console.error("Error: ", error);
    }
    setIsApplying(false);
  };

  return (
    <Modal
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      modalCss={{
        ...defaultModalCss,
        width: "400px",
        maxHeight: "650px",
        padding: "24px 0",
        gap: "32px",
      }}
      allowOutsideClick={false}
    >
      <Box className="flex flex-col gap-5">
        <ModalHeader
          modalType="others"
          HeaderIcon={<LinkIcon />}
          modalHeaderCss={{
            ...transactionLimitModalHeaderCss,
            paddingLeft: "24px",
            paddingRight: "24px",
          }}
        />
        <Box
          className={`px-6 flex flex-col gap-5 flex-shrink-0 overflow-x-hidden overflow-y-scroll ${!isSelectMenuOpen ? "h-[390px]" : "h-[480px]"}`}
        >
          <Box className="flex flex-col gap-2">
            <Heading3
              fontSize="18px"
              lineHeight="28px"
              textColor="#101828"
              fontWeight="600"
            >
              Total Freeze
            </Heading3>
            <Paragraph3
              lineHeight="20px"
              textColor="#475467"
            >
              Please confirm that you want to perform this action.
            </Paragraph3>
            <Box className="bg-[#FEF5E7] border border-[#FFEFD8] rounded-2xl p-3 flex items-center gap-[8px]">
              <InfoOutlineIcon className="flex-shrink-0" />
              <Paragraph3
                lineHeight="20px"
                textColor=" #1A202C"
              >
                This account will be unable to process any debit transactions.
              </Paragraph3>
            </Box>
          </Box>
          <Box>
            <StyledSelect
              options={OPTIONS_FOR_REASON_TO_DEACTIVATE_FREEZE_OR_CLOSE}
              name="reason"
              value={
                formData.reason
                  ? {
                      label: formData.reason,
                      value: formData.reason,
                    }
                  : null
              }
              handleOnChange={handleSelectChange}
              label="Reason for freeze"
              labelClassName="!font-semibold !leading-[20px] !text-[14px] !text-[#101828]"
              selectWrapperClassName="w-full"
              placeholder="Select a reason"
              customStyles={customStyledSelectStyles}
              menuPlacement="auto"
              setIsMenuOpen={setIsSelectMenuOpen}
            />
            <Maybe condition={formData.reason === "Others"}>
              <Box className="h-[179px] flex flex-col gap-[6px] mt-2">
                <Textarea
                  css={{
                    border: "1px solid #D0D5DD",
                    boxShadow: "box-shadow: 0px 1px 2px 0px #1018280D",
                    borderRadius: "8px",
                    height: "173px",
                    padding: "10px 16px",
                    fontSize: "14px",
                    resize: "none",

                    "&::placeholder": {
                      color: "#B1B9CA",
                    },
                  }}
                  placeholder="Enter your reason..."
                  onChange={handleReasonChange}
                  value={othersReason}
                />
              </Box>
            </Maybe>
          </Box>
          <Box>
            <FileUpload
              fileUploadLabel="Attachment (optional)"
              file={formData.attachment}
              name="attachment"
              handleFileChange={handleFileChange}
              doesUploadedFileContainerHaveFullWidth
              fileTypes={[
                "jpeg",
                "jpg",
                "png",
                "pdf",
                "doc",
                "docx",
                "xls",
                "xlsx",
                "csv",
              ]}
              uploadFileEmptyStateContainerCss={{
                height: "118px",
                flexShrink: "0",
              }}
              fileUploadLabelCss={{
                fontSize: "14px",
              }}
            />
          </Box>
        </Box>
      </Box>
      <Box className="flex items-center gap-3 px-6">
        <Button
          variant="outline"
          textColor="#344054"
          border="1px solid #D0D5DD"
          className="w-[170px]"
          onClick={handleCloseModal}
          disabled={isApplying || isUploading}
        >
          Cancel
        </Button>
        <Button
          variant="solid"
          textColor="#ffffff"
          border="1px solid #00535C"
          className="w-[170px]"
          onClick={handleApplyPostNoDebit}
          disabled={isFormDisabled}
          loading={isApplying || isUploading}
          loadingText="Freezing..."
        >
          Confirm
        </Button>
      </Box>
    </Modal>
  );
};
